/* eslint-disable @typescript-eslint/default-param-last */

import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { DataType } from "@/jenesys-types/data/enums";
import { AppError } from "@/jenesys-types/errors";
import {
  ListBaselineSorts,
  ListLocalStorageConfig,
  ListSearchCriteria,
  ListSearchOperator,
  ListSortCriteria
} from "@/jenesys-types/list";

import { Params, PathParams } from "@/services/api";

import { BasePayload, RelatedData } from "./types";

export interface ListPage {
  ids: Array<string>;
  relatedData: RelatedData;
}
export interface List {
  actionName: string;
  currentPageIndex: number;
  error?: AppError;
  filterCriteria?: ListSearchCriteria;
  isLoading: boolean;
  pages: Array<ListPage>;
  pathParams: PathParams;
  queryParams: Params;
  relationships: Array<string>;
  searchCriteria?: ListSearchCriteria;
  searchOperator: ListSearchOperator;
  sortCriteria?: ListSortCriteria;
  statusCounts?: Record<string, number>;
  lastChosenStatus?: string;
  statusesFilter?: string[];
  totalEntityCount: number;
  limit: number;
  offset: number;
  useSimpleSearch?: boolean;
  baselineSorts?: ListBaselineSorts;
  localStorageConfig?: ListLocalStorageConfig;
}
export interface ListState {
  lists: Record<string, List | undefined>;
}

export interface BaseListPayload extends BasePayload {
  listId: string;
}

interface CreateListPayload extends BaseListPayload {
  actionName: string;
  initialFilterCriteria?: ListSearchCriteria;
  initialSearchCriteria?: ListSearchCriteria;
  initialSortCriteria?: ListSortCriteria;
  pathParams: PathParams;
  queryParams: Params;
  relationships: Array<string>;
  searchOperator: ListSearchOperator;
  statusCounts?: Record<string, number>;
  useSimpleSearch?: boolean;
  baselineSorts?: ListBaselineSorts;
  localStorageConfig?: ListLocalStorageConfig;
}
export type CreateListAction = PayloadAction<CreateListPayload>;

export type ListLoadFailureAction = PayloadAction<
  BaseListPayload,
  string,
  never,
  AppError
>;

export type ListLoadStartedAction = PayloadAction<BaseListPayload>;

interface ListLoadSuccessPayload extends BaseListPayload {
  ids: Array<string>;
  pageIndex: number;
  relatedData: RelatedData;
  totalEntityCount: number;
  limit: number;
  offset: number;
  statusCounts?: Record<string, number>;
}
export type ListLoadSuccessAction = PayloadAction<ListLoadSuccessPayload>;

export type LoadListAction = PayloadAction<BaseListPayload>;

export type LoadListWithoutClearingAction = PayloadAction<BaseListPayload>;

export type LoadNextListPageAction = PayloadAction<BaseListPayload>;

interface LoadSpecificListPagePayload extends BaseListPayload {
  pageIndex: number;
}
export type LoadSpecificListPageAction =
  PayloadAction<LoadSpecificListPagePayload>;

export type RefreshListAction = PayloadAction<BaseListPayload>;

export type RemoveListAction = PayloadAction<BaseListPayload>;

interface SearchListPayload extends BaseListPayload {
  searchCriteria?: ListSearchCriteria;
}
export type SearchListAction = PayloadAction<SearchListPayload>;

interface SortListPayload extends BaseListPayload {
  sortCriteria?: ListSortCriteria;
}
export type SortListAction = PayloadAction<SortListPayload>;

interface FilterByStatusPayload extends BaseListPayload {
  status?: string;
  statuses?: string[];
}

export type FilterByStatusAction = PayloadAction<FilterByStatusPayload>;

export interface SetFilterCriteriaPayload {
  dataType: DataType;
  listId: string;
  filterCriteria?: ListSearchCriteria;
}
export type SetFilterCriteriaAction = PayloadAction<SetFilterCriteriaPayload>;

export interface UpdateQueryParamsPayload extends BaseListPayload {
  queryParams: Params;
}
export type UpdateQueryParamsAction = PayloadAction<UpdateQueryParamsPayload>;

const initialState: ListState = {
  lists: {}
};

const listSlice = createSlice({
  name: "crud/list",
  initialState,
  reducers: {
    createList: {
      reducer: (state: ListState, action: CreateListAction) => {
        const listState: List = {
          actionName: action.payload.actionName,
          currentPageIndex: 0,
          filterCriteria: action.payload.initialFilterCriteria,
          isLoading: false,
          pages: [],
          pathParams: action.payload.pathParams,
          queryParams: action.payload.queryParams,
          relationships: action.payload.relationships,
          searchCriteria: action.payload.initialSearchCriteria,
          searchOperator: action.payload.searchOperator,
          sortCriteria: action.payload.initialSortCriteria,
          totalEntityCount: 0,
          limit: 0,
          offset: 0,
          statusCounts: action.payload.statusCounts,
          useSimpleSearch: action.payload.useSimpleSearch,
          baselineSorts: action.payload.baselineSorts,
          localStorageConfig: action.payload.localStorageConfig
        };

        try {
          if (listState.localStorageConfig?.sortCriteria) {
            const sortCriteriaCached = JSON.parse(
              sessionStorage.getItem(
                localStorageKey({
                  prefix: listState.localStorageConfig.keyPrefix,
                  property: "sortCriteria"
                })
              ) ?? "{}"
            );
            const isValid =
              sortCriteriaCached &&
              "sortBy" in sortCriteriaCached &&
              typeof sortCriteriaCached.sortBy === "string" &&
              "sortOrder" in sortCriteriaCached &&
              ["ASC", "DESC"].includes(sortCriteriaCached.sortOrder);
            if (isValid) {
              listState.sortCriteria = sortCriteriaCached;
            }
          }
          if (listState.localStorageConfig?.searchCriteria) {
            const searchCriteriaCached = JSON.parse(
              sessionStorage.getItem(
                localStorageKey({
                  prefix: listState.localStorageConfig.keyPrefix,
                  property: "searchCriteria"
                })
              ) ?? "{}"
            );
            if (
              searchCriteriaCached &&
              typeof searchCriteriaCached === "object"
            ) {
              listState.searchCriteria =
                searchCriteriaCached as ListSearchCriteria;
            }
          }
          if (listState.localStorageConfig?.statusesFilter) {
            const statusesFilterCached = JSON.parse(
              sessionStorage.getItem(
                localStorageKey({
                  prefix: listState.localStorageConfig.keyPrefix,
                  property: "statusesFilter"
                })
              ) ?? "[]"
            );
            if (Array.isArray(statusesFilterCached)) {
              listState.statusesFilter = statusesFilterCached;
            }
          }
        } catch (e) {
          console.error(
            `Failed to load state from sessionStorage (${listState.localStorageConfig?.keyPrefix})`,
            e
          );
        }

        state.lists[action.payload.listId] = listState;
      },
      prepare: (
        dataType: DataType,
        listId: string,
        actionName: string = "list",
        pathParams: PathParams = {},
        queryParams: Params = {},
        relationships: Array<string> = [],
        initialSearchCriteria?: ListSearchCriteria,
        initialFilterCriteria?: ListSearchCriteria,
        searchOperator: ListSearchOperator = ListSearchOperator.And,
        initialSortCriteria?: ListSortCriteria,
        statusCounts?: Record<string, number>,
        useSimpleSearch?: boolean,
        baselineSorts?: ListBaselineSorts,
        localStorageConfig?: ListLocalStorageConfig
      ) => ({
        payload: {
          actionName,
          searchOperator,
          initialFilterCriteria,
          initialSearchCriteria,
          initialSortCriteria,
          dataType,
          listId,
          pathParams,
          queryParams,
          relationships,
          statusCounts,
          useSimpleSearch,
          baselineSorts,
          localStorageConfig
        }
      })
    },
    listLoadFailure: {
      reducer: (state: ListState, action: ListLoadFailureAction) => {
        const listState = state.lists[action.payload.listId];
        if (listState) {
          listState.error = action.error;
          listState.isLoading = false;
        }
      },
      prepare: (dataType: DataType, listId: string, error: AppError) => ({
        payload: { dataType, listId },
        error
      })
    },
    listLoadSuccess: {
      reducer: (state: ListState, action: ListLoadSuccessAction) => {
        const listState = state.lists[action.payload.listId];
        if (listState) {
          const {
            ids,
            pageIndex,
            relatedData,
            statusCounts,
            totalEntityCount,
            limit,
            offset
          } = action.payload;
          listState.currentPageIndex = pageIndex;
          listState.pages[pageIndex] = {
            ids,
            relatedData
          };
          listState.statusCounts = statusCounts;
          listState.totalEntityCount = totalEntityCount;
          listState.limit = limit;
          listState.offset = offset;
          listState.isLoading = false;
        }
      },
      prepare: (
        dataType: DataType,
        listId: string,
        ids: Array<string>,
        pageIndex: number,
        relatedData: RelatedData,
        totalEntityCount: number,
        limit: number,
        offset: number,
        statusCounts?: Record<string, number>
      ) => ({
        payload: {
          dataType,
          listId,
          ids,
          pageIndex,
          relatedData,
          totalEntityCount,
          limit,
          offset,
          statusCounts
        }
      })
    },
    loadList: {
      reducer: (state: ListState, action: LoadListAction) => {
        const listState = state.lists[action.payload.listId];
        if (listState) {
          listState.isLoading = true;
          listState.currentPageIndex = 0;
          listState.error = undefined;
          listState.pages = [];
        }
      },
      prepare: (dataType: DataType, listId: string) => ({
        payload: {
          dataType,
          listId
        }
      })
    },
    loadListWithoutClearing: {
      reducer: (state: ListState, action: LoadListWithoutClearingAction) => {
        const listState = state.lists[action.payload.listId];
        if (listState) {
          listState.isLoading = true;
          listState.error = undefined;
        }
      },
      prepare: (dataType: DataType, listId: string) => ({
        payload: {
          dataType,
          listId
        }
      })
    },
    loadNextListPage: {
      reducer: (state: ListState, action: LoadNextListPageAction) => {
        const listState = state.lists[action.payload.listId];
        if (listState) {
          listState.isLoading = true;
          listState.currentPageIndex += 1;
          listState.error = undefined;
        }
      },
      prepare: (dataType: DataType, listId: string) => ({
        payload: { dataType, listId }
      })
    },
    loadSpecificListPage: {
      reducer: (state: ListState, action: LoadSpecificListPageAction) => {
        const listState = state.lists[action.payload.listId];
        if (listState) {
          listState.isLoading = true;
          listState.currentPageIndex = action.payload.pageIndex;
          listState.error = undefined;
        }
      },
      prepare: (dataType: DataType, listId: string, pageIndex: number) => ({
        payload: { dataType, listId, pageIndex }
      })
    },
    refreshList: {
      reducer: (state: ListState, action: RefreshListAction) => {
        const listState = state.lists[action.payload.listId];
        if (listState) {
          listState.isLoading = true;
          listState.error = undefined;
        }
      },
      prepare: (dataType: DataType, listId: string) => ({
        payload: { dataType, listId }
      })
    },
    removeList: {
      reducer: (state: ListState, action: RemoveListAction) => {
        delete state.lists[action.payload.listId];
      },
      prepare: (dataType: DataType, listId: string) => ({
        payload: { dataType, listId }
      })
    },
    searchList: {
      reducer: (state: ListState, action: SearchListAction) => {
        const listState = state.lists[action.payload.listId];
        if (listState) {
          listState.isLoading = true;
          listState.currentPageIndex = 0;
          listState.error = undefined;
          listState.pages = [];
          listState.searchCriteria = action.payload.searchCriteria;

          if (
            listState.searchCriteria &&
            listState.localStorageConfig?.searchCriteria
          ) {
            try {
              sessionStorage.setItem(
                localStorageKey({
                  prefix: listState.localStorageConfig.keyPrefix,
                  property: "searchCriteria"
                }),
                JSON.stringify(listState.searchCriteria)
              );
            } catch (e) {
              console.error(
                `Failed to save searchCriteria to sessionStorage (${listState.localStorageConfig.keyPrefix})`,
                e
              );
            }
          }
        }
      },
      prepare: (
        dataType: DataType,
        listId: string,
        searchCriteria?: ListSearchCriteria
      ) => ({
        payload: {
          dataType,
          listId,
          searchCriteria
        }
      })
    },
    sortList: {
      reducer: (state: ListState, action: SortListAction) => {
        const listState = state.lists[action.payload.listId];
        if (listState) {
          listState.isLoading = true;
          listState.currentPageIndex = 0;
          listState.error = undefined;
          listState.pages = [];
          listState.sortCriteria = action.payload.sortCriteria;

          if (
            listState.sortCriteria &&
            listState.localStorageConfig?.sortCriteria
          ) {
            try {
              sessionStorage.setItem(
                localStorageKey({
                  prefix: listState.localStorageConfig.keyPrefix,
                  property: "sortCriteria"
                }),
                JSON.stringify(listState.sortCriteria)
              );
            } catch (e) {
              console.error(
                `Failed to save sortCriteria to sessionStorage (${listState.localStorageConfig.keyPrefix})`,
                e
              );
            }
          }
        }
      },
      prepare: (
        dataType: DataType,
        listId: string,
        sortCriteria?: ListSortCriteria
      ) => ({
        payload: {
          dataType,
          listId,
          sortCriteria
        }
      })
    },
    filterByStatus: {
      reducer: (state: ListState, action: FilterByStatusAction) => {
        const listState = state.lists[action.payload.listId];
        if (listState) {
          listState.isLoading = true;
          listState.currentPageIndex = 0;
          listState.error = undefined;
          listState.pages = [];
          listState.lastChosenStatus = action.payload.status;
          listState.statusesFilter = action.payload.statuses;
          listState.queryParams = { ...listState.queryParams };

          if (
            listState.statusesFilter &&
            listState.localStorageConfig?.statusesFilter
          ) {
            try {
              sessionStorage.setItem(
                localStorageKey({
                  prefix: listState.localStorageConfig.keyPrefix,
                  property: "statusesFilter"
                }),
                JSON.stringify(listState.statusesFilter)
              );
            } catch (e) {
              console.error(
                `Failed to save statusesFilter to sessionStorage (${listState.localStorageConfig.keyPrefix})`,
                e
              );
            }
          }

          // Set the status in Local Storage for document navigation
          if (
            action.payload.dataType === DataType.Transaction &&
            action.payload.status
          ) {
            // Only for transactions for now
            try {
              localStorage.setItem(
                "lastDocumentNavigationStatus",
                action.payload.status
              );
            } catch (e) {
              console.error(
                "Failed to set lastDocumentNavigationStatus in localStorage:",
                e
              );
            }
          }
        }
      },
      prepare: (
        dataType: DataType,
        listId: string,
        status?: string | string[]
      ) => ({
        payload:
          typeof status === "string"
            ? { dataType, listId, status }
            : { dataType, listId, statuses: status }
      })
    },
    setFilterCriteria: {
      reducer: (state: ListState, action: SetFilterCriteriaAction) => {
        const listState = state.lists[action.payload.listId];
        if (listState) {
          listState.isLoading = true;
          listState.currentPageIndex = 0;
          listState.error = undefined;
          listState.pages = [];
          listState.filterCriteria = action.payload.filterCriteria;
        }
      },
      prepare: (
        dataType: DataType,
        listId: string,
        filterCriteria?: ListSearchCriteria
      ) => ({
        payload: {
          dataType,
          listId,
          filterCriteria
        }
      })
    },
    updateQueryParams: {
      reducer: (state: ListState, action: UpdateQueryParamsAction) => {
        const listState = state.lists[action.payload.listId];
        if (listState) {
          listState.isLoading = true;
          listState.currentPageIndex = 0;
          listState.error = undefined;
          listState.pages = [];
          listState.queryParams = {
            ...listState.queryParams,
            ...action.payload.queryParams
          };
        }
      },
      prepare: (dataType: DataType, listId: string, queryParams: Params) => ({
        payload: {
          dataType,
          listId,
          queryParams
        }
      })
    }
  }
});

export const {
  createList,
  listLoadFailure,
  listLoadSuccess,
  loadList,
  loadListWithoutClearing,
  loadNextListPage,
  loadSpecificListPage,
  refreshList,
  removeList,
  searchList,
  sortList,
  filterByStatus,
  setFilterCriteria,
  updateQueryParams
} = listSlice.actions;

function localStorageKey(props: {
  prefix: string;
  property: "searchCriteria" | "statusesFilter" | "sortCriteria";
}) {
  return `listState_${props.prefix}_${props.property}`;
}

// Selectors

export const selectList = (state: ListState, listId: string) =>
  state.lists[listId];

export const selectStatusCounts = (state: ListState, listId: string) =>
  state.lists[listId]?.statusCounts;

export default listSlice.reducer;
